import React, { useState, useEffect } from 'react';
import { 
  ClockIcon, 
  UserGroupIcon, 
  ChartBarIcon, 
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import Button from '../components/ui/Button';

interface SessionStats {
  totalSessions: number;
  activeSessions: number;
  totalLoginsToday: number;
  totalLoginsThisWeek: number;
  totalLoginsThisMonth: number;
  averageSessionDuration: number;
  sessionsByUser: Array<{
    userId: string;
    userName: string;
    sessionCount: number;
    lastLogin: Date;
  }>;
}

const UserSessions: React.FC = () => {
  const [stats, setStats] = useState<SessionStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCleanupLoading, setIsCleanupLoading] = useState(false);

  useEffect(() => {
    loadSessionStats();
  }, []);

  const loadSessionStats = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiService.get('/user-sessions/stats');
      if (response.success) {
        setStats(response.data);
      } else {
        setError('Failed to load session statistics');
      }
    } catch (err) {
      setError('Failed to load session statistics');
      console.error('Error loading session stats:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Are you sure you want to cleanup old sessions? This action cannot be undone.')) {
      return;
    }

    try {
      setIsCleanupLoading(true);
      const response = await apiService.post('/user-sessions/cleanup', { daysOld: 90 });
      
      if (response.success) {
        alert(`Successfully cleaned up ${response.data.deletedCount} old sessions`);
        loadSessionStats(); // Reload stats
      } else {
        alert('Failed to cleanup old sessions');
      }
    } catch (err) {
      alert('Failed to cleanup old sessions');
      console.error('Error during cleanup:', err);
    } finally {
      setIsCleanupLoading(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
    return `${Math.round(seconds / 86400)}d`;
  };

  const formatDate = (dateString: string | Date): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return <div>No session data available</div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="User Sessions"
        subtitle="Monitor user login activity and session statistics"
        actions={
          <>
            <Button variant="secondary" icon={TrashIcon} disabled={isCleanupLoading} onClick={handleCleanup}>
              {isCleanupLoading ? 'Cleaning...' : 'Cleanup Old Sessions'}
            </Button>
            <Button variant="secondary" icon={ArrowDownTrayIcon} onClick={() => window.open('/api/user-sessions/export?format=csv', '_blank')}>
              Export
            </Button>
          </>
        }
      />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <StatCard label="Total Sessions" value={stats.totalSessions.toLocaleString()} icon={UserGroupIcon} tone="slate" />
        <StatCard label="Active Sessions" value={stats.activeSessions.toLocaleString()} icon={ChartBarIcon} tone="emerald" />
        <StatCard label="Logins Today" value={stats.totalLoginsToday.toLocaleString()} icon={ClockIcon} tone="blue" />
        <StatCard label="Avg Session" value={formatDuration(stats.averageSessionDuration)} icon={ClockIcon} tone="purple" />
      </div>

      {/* Login Activity Charts */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Login Activity</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">This Week</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{stats.totalLoginsThisWeek}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">This Month</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{stats.totalLoginsThisMonth}</span>
            </div>
          </div>
        </div>

        <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Session Overview</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Active Rate</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {stats.totalSessions > 0 
                  ? `${((stats.activeSessions / stats.totalSessions) * 100).toFixed(1)}%`
                  : '0%'
                }
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Users</span>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{stats.sessionsByUser.length}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Users by Session Count */}
      <div className="rounded-sm border border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Top Users by Session Count</h3>
        </div>
        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Session Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Last Login
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700">
              {stats.sessionsByUser.map((user, index) => (
                <tr key={user.userId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {user.userName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {user.sessionCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(user.lastLogin)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserSessions;
